import React from 'react';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';

export interface AvatarProps {
  variant: 'person' | 'company';
  firstName?: string;
  lastName?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
};

const iconSizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

const getInitials = (firstName?: string, lastName?: string): string => {
  if (!firstName && !lastName) return '?';
  
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';
  
  if (first && last) {
    return `${first[0]}${last[0]}`.toUpperCase();
  }
  
  if (first) {
    return first[0].toUpperCase();
  }
  
  if (last) {
    return last[0].toUpperCase();
  }
  
  return '?';
};

export const Avatar: React.FC<AvatarProps> = ({
  variant,
  firstName,
  lastName,
  size = 'md',
  className = '',
}) => {
  const baseClasses = 'rounded-full flex items-center justify-center font-semibold flex-shrink-0 border';
  const sizeClass = sizeClasses[size];
  const iconSize = iconSizeClasses[size];

  if (variant === 'person') {
    const initials = getInitials(firstName, lastName);
    return (
      <div
        className={`${baseClasses} ${sizeClass} bg-primary/10 text-primary border-primary/20 ${className}`}
        aria-label={`Avatar for ${firstName || ''} ${lastName || ''}`.trim()}
      >
        {initials}
      </div>
    );
  }

  return (
    <div
      className={`${baseClasses} ${sizeClass} bg-map-blue/10 text-map-blue border-map-blue/20 ${className}`}
      aria-label="Company email avatar"
    >
      <BuildingStorefrontIcon className={iconSize} aria-hidden="true" />
    </div>
  );
};

export default Avatar;
